import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CircleF, GoogleMap, MarkerF, useLoadScript } from '@react-google-maps/api';
import { Alert, Box, Button, CircularProgress, Stack, useMediaQuery, useTheme } from '@mui/material';
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_LOADER } from '../config/googleMaps';
import { darkMapStyles } from './MapRadiusPicker';

// Estilo inline: o layout do mapa não pode depender da injeção de CSS do sx
// (mesma solução do MapRadiusPicker).
export const DEFAULT_MAP_COLORS = {
  area: '#14B8A6',
  centerFill: '#14B8A6',
  centerStroke: '#042F2E',
  participant: '#3B82F6',
  participantStroke: '#F5F7FA',
  frame: 'rgba(68,224,198,0.2)',
};

const frameStyle = (height, border = DEFAULT_MAP_COLORS.frame) => ({
  position: 'relative',
  width: '100%',
  height,
  borderRadius: 8,
  overflow: 'hidden',
  border: `1px solid ${border}`,
  backgroundColor: '#0B1C3E',
  boxSizing: 'border-box',
});

const dotIcon = (fill, stroke) => ({
  url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
    `<svg width="22" height="22" viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg"><circle cx="11" cy="11" r="9" fill="${fill}" stroke="${stroke}" stroke-width="3"/></svg>`
  )}`,
  scaledSize: new window.google.maps.Size(22, 22),
  anchor: new window.google.maps.Point(11, 11),
});

function MapFrame({ center, reading, colors }) {
  const { isLoaded, loadError } = useLoadScript(GOOGLE_MAPS_LOADER);
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up('sm'));
  const height = wide ? 340 : 280;
  const [authFailed, setAuthFailed] = useState(false);
  const mapRef = useRef(null);
  const framedReading = useRef(false);

  useEffect(() => {
    const previous = window.gm_authFailure;
    window.gm_authFailure = () => setAuthFailed(true);
    return () => { window.gm_authFailure = previous; };
  }, []);

  const participant = useMemo(
    () => (reading ? { lat: reading.latitude, lng: reading.longitude } : null),
    [reading]
  );
  const icons = useMemo(() => (isLoaded && window.google?.maps
    ? {
      center: dotIcon(colors.centerFill, colors.centerStroke),
      participant: dotIcon(colors.participant, colors.participantStroke),
    }
    : null), [isLoaded, colors]);

  const frame = useCallback(() => {
    const map = mapRef.current;
    if (!map || !window.google?.maps) return;
    const bounds = new window.google.maps.LatLngBounds();
    if (center) {
      const area = new window.google.maps.Circle({ center, radius: center.radius || 50 }).getBounds();
      if (area) bounds.union(area);
    }
    if (participant) bounds.extend(participant);
    if (bounds.isEmpty()) return;
    map.fitBounds(bounds, 32);
  }, [center, participant]);

  const handleLoad = useCallback((map) => {
    mapRef.current = map;
    const start = center || participant;
    if (start) {
      map.setCenter(start);
      map.setZoom(17);
    }
    if (participant) framedReading.current = true;
    frame();
  // Apenas na montagem: o zoom não acompanha cada leitura nova.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Enquadra uma única vez quando a primeira leitura chega. Leituras
  // seguintes só movem o marcador, sem alterar o zoom escolhido.
  useEffect(() => {
    if (!participant || framedReading.current || !mapRef.current) return;
    framedReading.current = true;
    frame();
  }, [participant, frame]);

  if (loadError || authFailed) {
    return (
      <Alert severity="warning">
        O mapa não pôde ser carregado neste aparelho. A distância e a precisão abaixo continuam válidas e o servidor confere a localização ao confirmar.
      </Alert>
    );
  }
  if (!isLoaded) {
    return (
      <Box style={{ ...frameStyle(height, colors.frame), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress aria-label="Carregando mapa" />
      </Box>
    );
  }

  return (
    <Stack spacing={1}>
      <Box style={frameStyle(height, colors.frame)}>
        <GoogleMap
          onLoad={handleLoad}
          mapContainerStyle={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
          options={{
            disableDefaultUI: true,
            zoomControl: true,
            fullscreenControl: true,
            gestureHandling: 'greedy',
            clickableIcons: false,
            styles: darkMapStyles,
            backgroundColor: '#0B1C3E',
          }}
        >
          {center ? (
            <>
              <MarkerF position={center} icon={icons?.center} title="Centro do evento" />
              {center.radius ? (
                <CircleF
                  center={center}
                  radius={center.radius}
                  options={{ strokeColor: colors.area, strokeOpacity: 0.9, strokeWeight: 2, fillColor: colors.area, fillOpacity: 0.15, clickable: false }}
                />
              ) : null}
            </>
          ) : null}
          {participant ? (
            <>
              <MarkerF position={participant} icon={icons?.participant} title="Sua localização" />
              <CircleF
                center={participant}
                radius={Math.max(1, reading.accuracy)}
                options={{ strokeColor: colors.participant, strokeOpacity: 0.6, strokeWeight: 1, fillColor: colors.participant, fillOpacity: 0.1, clickable: false }}
              />
            </>
          ) : null}
        </GoogleMap>
      </Box>
      <Button size="small" variant="text" onClick={frame} sx={{ alignSelf: 'flex-start' }}>
        Enquadrar evento e minha posição
      </Button>
    </Stack>
  );
}

export default function PresenceMap({ center, reading, colors = DEFAULT_MAP_COLORS }) {
  if (!center && !reading) return null;
  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <Alert severity="info">
        Mapa indisponível nesta instalação. A distância e a precisão abaixo continuam válidas.
      </Alert>
    );
  }
  return <MapFrame center={center} reading={reading} colors={colors} />;
}
